import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '../../../shared/components/Button';
import { useToast } from '../../../shared/components/toast/useToast';
import { storeConfigApi } from '../api/storeConfigApi';

// Mirrors the hostname validation Watchman enforces server-side (UpdateCustomDomainDto).
const DOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;

const readErr = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

interface CustomDomainPanelProps {
  /** Watchman merchant id. */
  merchantId: string;
}

/**
 * Merchant custom production domain (e.g. brand.com) — the real domain the storefront serves on
 * and payment redirects land back on, in place of the default `<slug>.thecollectivesynergy.com`
 * subdomain. Reads/writes the same store-config record as BrandAssetsPanel, so the two panels'
 * caches stay in sync (shared `['store-config', merchantId]` query key).
 */
export function CustomDomainPanel({ merchantId }: CustomDomainPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const storeConfigKey = ['store-config', merchantId] as const;
  const { data: storeConfig, isLoading } = useQuery({
    queryKey: storeConfigKey,
    queryFn: () => storeConfigApi.get(merchantId),
    staleTime: 60 * 1000,
    enabled: !!merchantId,
  });

  const [domain, setDomain] = useState('');
  const hydrated = useRef(false);

  // Seed the input from the server once per merchant, so local edits aren't clobbered by refetches.
  useEffect(() => {
    hydrated.current = false;
  }, [merchantId]);
  useEffect(() => {
    if (storeConfig && !hydrated.current) {
      setDomain(storeConfig.customDomain ?? '');
      hydrated.current = true;
    }
  }, [storeConfig]);

  const saved = storeConfig?.customDomain ?? null;
  const trimmed = domain.trim();
  const isDirty = trimmed !== (saved ?? '');
  const isValid = trimmed === '' || DOMAIN_PATTERN.test(trimmed);

  const mutation = useMutation({
    mutationFn: (next: string | null) => storeConfigApi.setCustomDomain(merchantId, next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storeConfigKey });
      toast.success('Custom domain updated');
    },
    onError: (err: unknown) => toast.error(readErr(err, 'Failed to update custom domain')),
  });

  const handleSave = () => {
    if (!isValid || !isDirty) return;
    mutation.mutate(trimmed === '' ? null : trimmed);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-foreground">Custom Domain</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          This merchant's real production domain (e.g. <code>brand.com</code>). Once set, buyers land
          here after checkout instead of the default subdomain. Leave blank to clear it.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="brand.com"
              className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground font-mono"
            />
            {!isValid && (
              <p className="text-[11px] text-red-500 mt-1">
                Enter a valid hostname (e.g. brand.com), or leave blank to clear it.
              </p>
            )}
            {saved && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Currently set to <span className="font-mono">{saved}</span>.
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="primary"
            loading={mutation.isPending}
            disabled={mutation.isPending || !isDirty || !isValid}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
